import {
    AppDispatch,
    SidePanelLayerOrGroup,
    SidePanelLayerType,
    UpdateAction,
    zIndex,
} from "@nova/constants"
import {
    createFractionalIndexBetween,
    END,
    START,
} from "@nova/serializer/src/client/util/fractional_indexing"
import classNames from "classnames"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import { useCallback, useEffect, useMemo, useRef } from "react"
import { DragPreviewImage, useDrag, useDragLayer, useDrop } from "react-dnd"
import { dataSelectorsByMode, maxDepthByMode, SidePanelMode } from "./constants"
import { selectRenamingLayerId, selectSelectedLayerIds, selectSelectedLayers } from "./slice.new"
import { getSiblings } from "./utils"
import {
    getLocalStorageValue,
    setLocalStorageValue,
    useLocalStorageValue,
} from "tools/store/localStorageRegistry"
import store from "integrations/redux/store"
import { RootState } from "integrations/redux"
import { useIsEditor } from "tools/hooks/useIsEditor"
import { getSidePanelLayerTypeToUpdateFunctionMap } from "plugins/plugin-registry"

//a fully transparent image for the drag preview, so that there is no drag preview
const previewImageURL = "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///ywAAAAAAQABAAACAUwAOw=="

const expandOnHoverDelayMs = 550

export function useSidePanelDragAndDrop({
    entityId,
    mode,
    updateFn,
    shortenIndicator,
    sortedEntities,
}: {
    entityId: string
    mode: SidePanelMode
    updateFn:
        | UpdateAction
        | ((params: {
              id: string
              updates: Record<string, unknown>
          }) => (dispatch: AppDispatch, getState: () => RootState) => void)
    shortenIndicator: boolean
    sortedEntities: SidePanelLayerOrGroup[]
}) {
    const sortMode = useAppSelector(dataSelectorsByMode[mode].selectSortMode)

    const dispatch = useAppDispatch()
    const renamingLayerId = useAppSelector(selectRenamingLayerId)
    const isRenaming = renamingLayerId === entityId
    const isEditor = useIsEditor()
    const expandedLayerIds = useLocalStorageValue("sidePanelExpandedLayerIds")

    const getLayerDepth = useCallback(
        (id: string, base = 0): number => {
            const state = store.getState()
            const entityMap = dataSelectorsByMode[mode].selectEntityMap(state)
            if (!entityMap[id].parentId || entityMap[id].parentId === "null") {
                return base
            }
            return 1 + getLayerDepth(entityMap[id].parentId, base)
        },
        [mode]
    )
    const getChildrenDepth = useCallback(
        (id: string, depth = 0): number => {
            const state = store.getState()
            const entityMap = dataSelectorsByMode[mode].selectEntityMap(state)
            if (!entityMap[id].children?.length) {
                return depth
            }
            return Math.max(
                ...entityMap[id].children.map((child) => getChildrenDepth(child.id, depth + 1))
            )
        },
        [mode]
    )
    const isDraggingIntoSelf = useCallback(
        (dragId: string, dropBelowId: string, depth = 0): boolean => {
            if (dragId === dropBelowId && depth) return true
            const state = store.getState()
            const entityMap = dataSelectorsByMode[mode].selectEntityMap(state)
            const parent = entityMap[dropBelowId]?.parentId
            return parent ? isDraggingIntoSelf(dragId, parent, depth + 1) : false
        },
        [mode]
    )

    const applyDrop = useCallback(
        (item: { entityId: string; updateFn: UpdateAction }, dropZone: "top" | "bottom") => {
            const state = store.getState()
            const dropContext = {
                entitiesById: dataSelectorsByMode[mode].selectEntityMap(state),
                sortedEntities,
            }
            const { updates, start, end } =
                dropZone === "top"
                    ? getDropUpdates({ ...dropContext, entityBelowId: entityId })
                    : getDropUpdates({ ...dropContext, entityAboveId: entityId })

            const selectedLayerIds = selectSelectedLayerIds(state)
            if (selectedLayerIds.length < 2 || !selectedLayerIds.includes(item.entityId)) {
                dispatch(item.updateFn({ id: item.entityId, updates }))
                return
            }

            const depthOfDropLocation = getLayerDepth(
                entityId,
                dropZone === "bottom" && shortenIndicator ? 1 : 0
            )
            const movingLayers = selectSelectedLayers(state).flatMap((layer) =>
                layer.type &&
                !isDraggingIntoSelf(layer.id, entityId) &&
                depthOfDropLocation + getChildrenDepth(layer.id) <= maxDepthByMode[mode]
                    ? [{ id: layer.id, type: layer.type }]
                    : []
            )
            const updateFunctionByLayerType = getSidePanelLayerTypeToUpdateFunctionMap()

            // A larger index renders higher, so stack the top-down selection from the bottom up.
            let index = start
            movingLayers.reverse().forEach((layer) => {
                index = createFractionalIndexBetween(index, end)
                dispatch(
                    updateFunctionByLayerType[layer.type]({
                        id: layer.id,
                        updates: { ...updates, index },
                    })
                )
            })
        },
        [
            dispatch,
            entityId,
            getChildrenDepth,
            getLayerDepth,
            isDraggingIntoSelf,
            mode,
            shortenIndicator,
            sortedEntities,
        ]
    )

    const [{ isDragging }, dragRef, preview] = useDrag(
        () => ({
            type: mode,
            item: { entityId, updateFn },
            collect: (monitor) => ({
                isDragging: !!monitor.isDragging(),
            }),
            canDrag: !isRenaming && isEditor && sortMode === "None",
        }),
        [entityId, isRenaming, isEditor, sortMode]
    )
    const [{ isOver: isOverTop, canDrop: canDropTop }, dropRefTop] = useDrop(
        () => ({
            accept: mode,
            drop: (item: { entityId: string; updateFn: UpdateAction }) => applyDrop(item, "top"),
            canDrop: ({ entityId: draggingItemEntityId }) => {
                const depthOfDropLocation = getLayerDepth(entityId)
                const depthOfDraggingItem = getChildrenDepth(draggingItemEntityId)
                return (
                    depthOfDropLocation + depthOfDraggingItem <= maxDepthByMode[mode] &&
                    !isDraggingIntoSelf(draggingItemEntityId, entityId)
                )
            },
            collect: (monitor) => ({
                isOver: !!monitor.isOver(),
                canDrop: !!monitor.canDrop(),
            }),
        }),
        [applyDrop, entityId, mode, sortedEntities]
    )
    const [{ isOver: isOverBottom, canDrop: canDropBottom }, dropRefBottom] = useDrop(
        () => ({
            accept: mode,
            drop: (item: { entityId: string; updateFn: UpdateAction }) => applyDrop(item, "bottom"),
            canDrop: ({ entityId: draggingItemEntityId }) => {
                // shortenIndicator tells us we are dropping as a child of the drop location's entity, not a sibling.
                // So, we need the base layer depth to be 1, not 0.
                const depthOfDropLocation = getLayerDepth(entityId, shortenIndicator ? 1 : 0)
                const depthOfDraggingItem = getChildrenDepth(draggingItemEntityId)
                return (
                    depthOfDropLocation + depthOfDraggingItem <= maxDepthByMode[mode] &&
                    !isDraggingIntoSelf(draggingItemEntityId, entityId)
                )
            },
            collect: (monitor) => ({
                isOver: !!monitor.isOver(),
                canDrop: !!monitor.canDrop(),
            }),
        }),
        [applyDrop, entityId, mode, sortedEntities]
    )
    const expandTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)
    useEffect(() => {
        const isCollapsedGroup = shortenIndicator && !expandedLayerIds.includes(entityId)
        if (!isOverBottom || !canDropBottom || !isCollapsedGroup) return
        expandTimeoutRef.current = setTimeout(() => {
            const currentExpandedLayerIds = getLocalStorageValue("sidePanelExpandedLayerIds")
            if (!currentExpandedLayerIds.includes(entityId)) {
                setLocalStorageValue("sidePanelExpandedLayerIds", [
                    ...currentExpandedLayerIds,
                    entityId,
                ])
            }
        }, expandOnHoverDelayMs)
        return () => clearTimeout(expandTimeoutRef.current)
    }, [canDropBottom, entityId, expandedLayerIds, isOverBottom, shortenIndicator])
    const anythingIsDragging = useDragLayer((monitor) => {
        const type = monitor.getItemType()
        const isAcceptableType = type === mode
        return monitor.isDragging() && isAcceptableType
    })
    const DragPreviewComponent = useCallback(
        () => <DragPreviewImage connect={preview} src={previewImageURL} />,
        [preview]
    )
    const dropTopRef = useRef<HTMLDivElement>(null)
    dropRefTop(dropTopRef)
    const dropBottomRef = useRef<HTMLDivElement>(null)
    dropRefBottom(dropBottomRef)
    const TopDropComponent = useMemo(
        () => getDropComponent({ dropRef: dropTopRef, type: "top" }),
        [dropTopRef]
    )
    const BottomDropComponent = useMemo(
        () =>
            getDropComponent({
                dropRef: dropBottomRef,
                type: "bottom",
                shortenIndicator,
            }),
        [dropBottomRef, shortenIndicator]
    )
    return {
        dragRef,
        TopDropComponent,
        BottomDropComponent,
        DragPreviewComponent,
        isDragging,
        isOverTop,
        canDropTop,
        isOverBottom,
        canDropBottom,
        anythingIsDragging,
    }
}

function getDropComponent({
    dropRef,
    type,
    shortenIndicator,
}: {
    dropRef: React.RefObject<HTMLDivElement | null>
    type: "top" | "bottom"
    shortenIndicator?: boolean
}) {
    const DropComponent = ({
        isDragging,
        isOver,
        canDrop,
        anythingIsDragging,
    }: {
        isDragging: boolean
        isOver: boolean
        canDrop: boolean
        anythingIsDragging: boolean
    }) => {
        if (!anythingIsDragging || isDragging) return null
        return (
            <div
                ref={dropRef}
                style={{ zIndex: zIndex.SidePanel + 50 }}
                className={classNames("absolute left-0 right-0 flex justify-center", {
                    "justify-center": !shortenIndicator,
                    "justify-end pl-6": shortenIndicator,
                    "-top-px bottom-1/2 items-start": type === "top",
                    "top-1/2 -bottom-px items-end": type === "bottom",
                })}
            >
                <div
                    className={classNames("w-full h-[2px]", {
                        "-translate-y-px": type === "top",
                        "translate-y-px": type === "bottom",
                        "opacity-1": isOver,
                        "opacity-0": !isOver,
                        "bg-white-800": canDrop,
                        "bg-red-500": !canDrop,
                    })}
                />
            </div>
        )
    }
    return DropComponent
}

function getDropUpdates({
    entitiesById,
    sortedEntities,
    entityAboveId,
    entityBelowId,
}: {
    entitiesById: Record<string, SidePanelLayerOrGroup>
    sortedEntities: SidePanelLayerOrGroup[]
} & (
    | { entityAboveId?: never; entityBelowId: string }
    | { entityAboveId: string; entityBelowId?: never }
)) {
    let start: string
    let end: string
    let newParentId: string | undefined

    if (entityAboveId) {
        const entityAbove = entitiesById[entityAboveId]
        if (entityAbove.type === SidePanelLayerType.Group) {
            // Drop inside the group at the start
            const siblings = entityAbove.children || []
            if (siblings.length) {
                start = siblings.at(0)!.index
            } else {
                start = START
            }
            end = END
            newParentId = entityAbove.id
        } else {
            const siblings = getSiblings(entityAboveId, entitiesById, sortedEntities)
            const entityAboveNumericalIndexInSiblings = siblings.findIndex(
                (entity) => entity.id === entityAboveId
            )
            if (entityAboveNumericalIndexInSiblings === siblings.length - 1) {
                start = START
            } else {
                start = siblings.at(entityAboveNumericalIndexInSiblings + 1)!.index
            }
            end = entityAbove.index
            newParentId = entityAbove.parentId
        }
    } else {
        const belowId = entityBelowId!
        const entityBelow = entitiesById[belowId]
        const siblings = getSiblings(belowId, entitiesById, sortedEntities)
        const entityBelowNumericalIndexInSiblings = siblings.findIndex(
            (entity) => entity.id === belowId
        )
        if (entityBelowNumericalIndexInSiblings === 0) {
            end = END
        } else {
            end = siblings.at(entityBelowNumericalIndexInSiblings - 1)!.index
        }
        start = entityBelow.index
        newParentId = entityBelow.parentId
    }

    const updates = {
        index: createFractionalIndexBetween(start, end),
        parentId: newParentId || "null", // prototodo
    }
    return { updates, start, end }
}

// This component gives extra room at the top and bottom of lists with drag and drop
// It is needed so the drop preview lines can show for the top and bottom drop zones
export function DragAndDroppableListBuffer() {
    return <div className="w-full h-[2px]" />
}
